if (!customElements.get("collection-list")) {
  class CollectionList extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: ".collection-list__items",
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
        scrollbar: ".collection-list-scrollbar",
        scrollbarThumb: ".collection-list-scrollbar__thumb",
      };

      this.classes = {
        grid: "f-grid",
        swiper: "swiper",
        swiperWrapper: "swiper-wrapper",
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);
      this.cardStyle = this.dataset.cardStyle;
      this.slidesPerView = parseFloat(this.dataset.slidesPerView) || 7.5;
      this.scrollbarEl = this.section.querySelector(this.selectors.scrollbar);
      this.scrollbarThumb = this.section.querySelector(
        this.selectors.scrollbarThumb,
      );

      this.sliderInstance = false;
      this._mobileScrollHandler = null;

      if (this.scrollbarEl) {
        this._setupScrollbarInteraction();
      }

      const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
      mql.onchange = this.init.bind(this);
      this.init();
    }

    init() {
      if (FoxTheme.config.mqlMobile) {
        this.destroySlider();
      } else {
        this.initSlider();
      }
    }

    initSlider() {
      if (this._mobileScrollHandler) {
        this.removeEventListener("scroll", this._mobileScrollHandler);
        this._mobileScrollHandler = null;
      }

      const sliderOptions = {
        modules: [
          FoxTheme.Swiper.Navigation,
          FoxTheme.Swiper.Pagination,
          FoxTheme.Swiper.Mousewheel,
        ],
        slidesPerView: this.slidesPerView,
        spaceBetween: 8,
        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },
        pagination: false,
        loop: false,
        threshold: 2,
        mousewheel: {
          enabled: true,
          forceToAxis: true,
        },
        on: {
          progress: (swiper, progress) => this._updateScrollbar(progress),
          afterInit: (swiper) => this._updateScrollbar(swiper.progress),
          resize: (swiper) => this._updateScrollbar(swiper.progress),
        },
        breakpoints: {
          768: {
            spaceBetween: this.cardStyle == "card" ? 8 : 20,
            slidesPerView: this.slidesPerView > 4 ? 4 : this.slidesPerView,
          },
          1024: {
            spaceBetween: this.cardStyle == "card" ? 8 : 20,
            slidesPerView:
              this.slidesPerView > 6
                ? this.slidesPerView - 1
                : this.slidesPerView,
          },
          1280: {
            spaceBetween: this.cardStyle == "card" ? 8 : 20,
            slidesPerView: this.slidesPerView,
          },
        },
      };

      if (typeof this.sliderInstance !== "object") {
        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);

        this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions);
        this.sliderInstance.init();

        const focusableElements = FoxTheme.a11y.getFocusableElements(this);

        focusableElements.forEach((element) => {
          element.addEventListener("focusin", () => {
            const slide = element.closest(".swiper-slide");
            this.sliderInstance.slider.slideTo(
              this.sliderInstance.slider.slides.indexOf(slide),
            );
          });
        });

        if (Shopify.designMode) {
          document.addEventListener("shopify:block:select", (e) => {
            if (e.detail.sectionId != this.sectionId) return;
            let { target } = e;
            const index = Number(target.dataset.index);

            this.sliderInstance.slider.slideTo(index);
          });
        }
      }
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.classes.grid);
      if (typeof this.sliderInstance === "object") {
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }

      if (this.scrollbarEl && !this._mobileScrollHandler) {
        this._mobileScrollHandler = () => {
          const max = this.scrollWidth - this.clientWidth;
          this._updateScrollbar(max > 0 ? this.scrollLeft / max : 0);
        };
        this.addEventListener("scroll", this._mobileScrollHandler, {
          passive: true,
        });
        this._updateScrollbar(0);
      }
    }

    _getThumbWidth() {
      const slider = this.sliderInstance?.slider;
      if (slider && slider.slides?.length > 0) {
        const visible = slider.size;
        const total = slider.virtualSize;
        if (total > visible) {
          return Math.min(100, Math.max(10, (visible / total) * 100));
        }
        return 100;
      }
      const total = this.scrollWidth;
      const visible = this.clientWidth;
      if (total > visible) {
        return Math.min(100, Math.max(10, (visible / total) * 100));
      }
      return 100;
    }

    _updateScrollbar(progress) {
      if (!this.scrollbarThumb) return;
      if (this._updateActiveIndicator()) return;
      const thumbPct = this._getThumbWidth();
      const maxLeft = 100 - thumbPct;
      this.scrollbarThumb.style.width = `${thumbPct}%`;
      this.scrollbarThumb.style.left = `${Math.max(0, Math.min(1, progress)) * maxLeft}%`;
      this.scrollbarEl.style.display = thumbPct >= 100 ? "none" : "";
    }

    _updateActiveIndicator() {
      const activeSlide = this.querySelector(".swiper-slide.is-active");
      if (!activeSlide || !this.scrollbarEl) return false;
      const trackRect = this.scrollbarEl.getBoundingClientRect();
      if (trackRect.width <= 0) return false;
      const cardRect = activeSlide.getBoundingClientRect();
      let left = cardRect.left - trackRect.left;
      let width = cardRect.width;
      if (left < 0) {
        width += left;
        left = 0;
      }
      if (left + width > trackRect.width) {
        width = trackRect.width - left;
      }
      width = Math.max(0, width);
      this.scrollbarEl.style.display = "";
      this.scrollbarThumb.style.width = `${(width / trackRect.width) * 100}%`;
      this.scrollbarThumb.style.left = `${(left / trackRect.width) * 100}%`;
      return true;
    }

    _setupScrollbarInteraction() {
      let isDragging = false;

      const getProgress = (clientX) => {
        const rect = this.scrollbarEl.getBoundingClientRect();
        const thumbPx = (this._getThumbWidth() / 100) * rect.width;
        const usable = rect.width - thumbPx;
        if (usable <= 0) return 0;
        return Math.max(
          0,
          Math.min(1, (clientX - rect.left - thumbPx / 2) / usable),
        );
      };

      const applyProgress = (progress) => {
        if (this.sliderInstance?.slider) {
          this.sliderInstance.slider.setProgress(progress, 0);
        } else {
          const max = this.scrollWidth - this.clientWidth;
          this.scrollLeft = progress * max;
        }
      };

      this.scrollbarEl.addEventListener("pointerdown", (e) => {
        isDragging = true;
        if (this.scrollbarThumb) this.scrollbarThumb.style.transition = "none";
        applyProgress(getProgress(e.clientX));
        e.preventDefault();
      });

      document.addEventListener("pointermove", (e) => {
        if (isDragging) applyProgress(getProgress(e.clientX));
      });

      document.addEventListener("pointerup", () => {
        if (isDragging) {
          isDragging = false;
          if (this.scrollbarThumb) this.scrollbarThumb.style.transition = "";
        }
      });
    }
  }
  customElements.define("collection-list", CollectionList);
}
